import { avgKeys, clamp } from '../../LocalEmotionUtils';
import { resolveYawnEvidenceProvider, type YawnEvidenceContext } from './YawnEvidenceProvider';

export interface MinimalCalibrationBaseline {
  jawOpen?: { p10: number; p90: number };
}

export interface YawnEvaluationInput {
  blendshapes: Record<string, number>;
  mar: number;
  ear: number;
  isMobileRuntime: boolean;
  handNearFace: boolean;
  handNearMouth: boolean;
  /** Mutable buffers — caller owns these across frames */
  yawnBuffer: number[];
  yawnMarBuffer: number[];
  yawnEarBuffer: number[];
  yawnHoldFrames: number;
  setYawnHoldFrames: (frames: number) => void;
  /** Thresholds */
  yawnThreshold: number;
  yawnHoldFramesLimit: number;
  yawnMarThreshold: number;
  yawnEarThreshold: number;
  yawnWindow: number;
  yawnRequired: number;
  /** Optional */
  baseline?: MinimalCalibrationBaseline | null;
  lowLightSeverity?: number;
}

export interface YawnFrameInput {
  blendshapes: Record<string, number>;
  mar: number;
  ear: number;
  handNearFace: boolean;
  handNearMouth: boolean;
  lowLightSeverity?: number;
}

export interface YawnDetectorConfig {
  isMobileRuntime: boolean;
  yawnThreshold?: number;
  yawnHoldFramesLimit?: number;
  yawnMarThreshold?: number;
  yawnEarThreshold?: number;
  yawnWindow?: number;
  yawnRequired?: number;
}

/**
 * Stateful yawn detector — owns buffers, hold frames, and config.
 * Caller only needs to pass per-frame data via evaluate().
 */
export class YawnDetectorState {
  private buffer: number[] = [];
  private marBuffer: number[] = [];
  private earBuffer: number[] = [];
  private holdFrames = 0;
  private readonly isMobileRuntime: boolean;
  private readonly threshold: number;
  private readonly holdFramesLimit: number;
  private readonly marThreshold: number;
  private readonly earThreshold: number;
  private readonly window: number;
  private readonly required: number;

  constructor(config: YawnDetectorConfig) {
    this.isMobileRuntime = config.isMobileRuntime;
    this.threshold = config.yawnThreshold ?? 0.38;
    this.holdFramesLimit = config.yawnHoldFramesLimit ?? 36;
    this.marThreshold = config.isMobileRuntime
      ? (config.yawnMarThreshold ?? 0.55)
      : (config.yawnMarThreshold ?? 0.66);
    this.earThreshold = config.isMobileRuntime
      ? (config.yawnEarThreshold ?? 0.26)
      : (config.yawnEarThreshold ?? 0.23);
    this.window = config.yawnWindow ?? 4;
    this.required = config.yawnRequired ?? 2;
  }

  evaluate(frame: YawnFrameInput): { yawnScore: number; yawnDetected: boolean } {
    return evaluateYawn({
      blendshapes: frame.blendshapes,
      mar: frame.mar,
      ear: frame.ear,
      isMobileRuntime: this.isMobileRuntime,
      handNearFace: frame.handNearFace,
      handNearMouth: frame.handNearMouth,
      yawnBuffer: this.buffer,
      yawnMarBuffer: this.marBuffer,
      yawnEarBuffer: this.earBuffer,
      yawnHoldFrames: this.holdFrames,
      setYawnHoldFrames: (f) => { this.holdFrames = f; },
      yawnThreshold: this.threshold,
      yawnHoldFramesLimit: this.holdFramesLimit,
      yawnMarThreshold: this.marThreshold,
      yawnEarThreshold: this.earThreshold,
      yawnWindow: this.window,
      yawnRequired: this.required,
      lowLightSeverity: frame.lowLightSeverity,
    });
  }

  reset(): void {
    this.buffer.length = 0;
    this.marBuffer.length = 0;
    this.earBuffer.length = 0;
    this.holdFrames = 0;
  }
}

/**
 * Evaluates yawn from blendshapes + geometry metrics.
 * Returns { yawnScore, yawnDetected } and calls setYawnHoldFrames to update caller-held state.
 */
export function evaluateYawn(input: YawnEvaluationInput): { yawnScore: number; yawnDetected: boolean } {
  const {
    blendshapes,
    mar,
    ear,
    isMobileRuntime,
    handNearFace,
    handNearMouth,
    yawnBuffer,
    yawnMarBuffer,
    yawnEarBuffer,
    yawnHoldFrames,
    setYawnHoldFrames,
    yawnThreshold,
    yawnHoldFramesLimit,
    yawnMarThreshold,
    yawnEarThreshold,
    yawnWindow,
    yawnRequired,
    baseline,
    lowLightSeverity,
  } = input;

  const isLowLight = (lowLightSeverity ?? 0) > 0.3;
  // Mobile IMAGE mode produces compressed MAR range (~0.33 rest → 0.48 yawn)
  // vs desktop VIDEO mode (~0.05 → 0.70). Scale thresholds accordingly.
  const effectiveMarThreshold = isMobileRuntime ? yawnMarThreshold * 0.68 : yawnMarThreshold;
  const effectiveEarThreshold = isMobileRuntime ? Math.min(0.32, yawnEarThreshold * 1.30) : yawnEarThreshold;
  const effectiveYawnThreshold = isMobileRuntime ? yawnThreshold * 0.82 : yawnThreshold;
  const eyeSquintYawn = avgKeys(blendshapes, ['eyeSquintLeft', 'eyeSquintRight']);
  const mouthStretch = avgKeys(blendshapes, ['mouthStretchLeft', 'mouthStretchRight']);
  const jawOpen = blendshapes['jawOpen'] ?? 0;
  const eyeWide = avgKeys(blendshapes, ['eyeWideLeft', 'eyeWideRight']);
  const browRaise = avgKeys(blendshapes, ['browInnerUp', 'browOuterUpLeft', 'browOuterUpRight']);
  const browDown = avgKeys(blendshapes, ['browDownLeft', 'browDownRight']);

  const hasGeometrySample = Number.isFinite(mar) && mar > 0 && Number.isFinite(ear) && ear > 0;
  if (hasGeometrySample) {
    yawnMarBuffer.push(mar);
    if (yawnMarBuffer.length > yawnWindow) yawnMarBuffer.shift();
    yawnEarBuffer.push(ear);
    if (yawnEarBuffer.length > yawnWindow) yawnEarBuffer.shift();
  }

  const avgMar = yawnMarBuffer.length > 0
    ? yawnMarBuffer.reduce((a, b) => a + b, 0) / yawnMarBuffer.length
    : 0;
  const avgEar = yawnEarBuffer.length > 0
    ? yawnEarBuffer.reduce((a, b) => a + b, 0) / yawnEarBuffer.length
    : 0;
  const hasReliableGeometry = hasGeometrySample && yawnMarBuffer.length > 0 && yawnEarBuffer.length > 0;
  // Choose provider from real signal quality, not platform, so sessions without MAR/EAR can still detect yawns.
  const useGeometryProvider = hasReliableGeometry;
  const evidenceContext: YawnEvidenceContext = {
    jawOpen,
    mouthStretch,
    eyeSquint: eyeSquintYawn,
    eyeWide,
    browRaise,
    browDown,
    avgMar,
    avgEar,
    yawnThreshold: effectiveYawnThreshold,
    yawnMarThreshold: effectiveMarThreshold,
    yawnEarThreshold: effectiveEarThreshold,
  };
  const evidenceProvider = resolveYawnEvidenceProvider(useGeometryProvider);
  const effectiveYawnRequired = evidenceProvider.id === 'blendshape-fallback'
    ? Math.max(1, yawnRequired - 1)
    : yawnRequired;

  let yawnScore = clamp(evidenceProvider.computeYawnScore(evidenceContext), 0, 1);

  yawnBuffer.push(yawnScore);
  if (yawnBuffer.length > yawnWindow) yawnBuffer.shift();
  const voteThreshold = evidenceProvider.getVoteThreshold(effectiveYawnThreshold);
  const over = yawnBuffer.filter(v => v >= voteThreshold).length;

  const _log = typeof window !== 'undefined' && (window as any)._log ? (window as any)._log : console.log;
  if (baseline && baseline.jawOpen) {
    _log(`[EIE][YAWN-CALIB] Baseline jawOpen (MAR) p10=${baseline.jawOpen.p10.toFixed(3)} p90=${baseline.jawOpen.p90.toFixed(3)} | marThres=${yawnMarThreshold.toFixed(3)} | earThres=${yawnEarThreshold.toFixed(3)}`);
  }
  _log(`[EIE][YAWN-VAL] mar=${mar.toFixed(3)} avgMar=${avgMar.toFixed(3)} ear=${ear.toFixed(3)} avgEar=${avgEar.toFixed(3)} yawnScore=${yawnScore.toFixed(3)} yawnThres=${yawnThreshold.toFixed(3)}`);

  const marCondition = evidenceProvider.hasMarEvidence(evidenceContext);
  const earCondition = evidenceProvider.hasEarEvidence(evidenceContext);

  if (evidenceProvider.shouldIncrementHold(evidenceContext)) {
    setYawnHoldFrames(Math.min(yawnHoldFrames + 1, yawnHoldFramesLimit));
  } else {
    setYawnHoldFrames(Math.max(0, yawnHoldFrames - 1));
  }

  const durationYawning = yawnHoldFrames >= yawnHoldFramesLimit;
  const handMouthGesture = !!handNearMouth;

  const eyeWideActive = eyeWide > 0.12;
  const surpriseProfile = browRaise > 0.10 && (blendshapes['jawOpen'] ?? 0) > 0.26 && eyeSquintYawn < 0.22;
  const notYawnProfile = eyeWideActive || surpriseProfile;

  const surpriseLikeOpenMouth = evidenceProvider.isSurpriseLikeOpenMouth(evidenceContext);

  const partialYawnWhileCovering = !!handNearFace && avgMar > yawnMarThreshold * 0.80 && !notYawnProfile && !surpriseLikeOpenMouth;
  const marEvidence = evidenceProvider.hasMarEvidence(evidenceContext);
  const handYawnBoost = (handMouthGesture || partialYawnWhileCovering) ? 0.22 : 0;
  const strongJawOnlyEvidence = isMobileRuntime && jawOpen >= 0.50;
  const mobileSquintThreshold = isLowLight ? 0.22 : 0.35;
  const mobileJawSquintEvidence = isMobileRuntime && jawOpen >= 0.38 && eyeSquintYawn >= mobileSquintThreshold;
  const mobileGeometryYawn = isMobileRuntime && marEvidence && evidenceProvider.hasEarEvidence(evidenceContext) && eyeSquintYawn >= mobileSquintThreshold && jawOpen >= 0.35;
  const yawnSpecificEvidence = handMouthGesture || mouthStretch >= 0.10 || eyeSquintYawn >= 0.18 || strongJawOnlyEvidence || mobileJawSquintEvidence;
  const shouldApplyFallbackScoreFloor = evidenceProvider.id === 'blendshape-fallback'
    && !notYawnProfile
    && !surpriseLikeOpenMouth
    && (jawOpen > 0.25 || mouthStretch > 0.18 || handMouthGesture || partialYawnWhileCovering);
  if (shouldApplyFallbackScoreFloor) {
    const flooredFallbackScore = clamp(Math.max(yawnScore, 0.45 + handYawnBoost), 0, 1);
    if (flooredFallbackScore !== yawnScore) {
      yawnScore = flooredFallbackScore;
      if (yawnBuffer.length > 0) {
        yawnBuffer[yawnBuffer.length - 1] = flooredFallbackScore;
      }
    }
  }

  const overWithJawEvidence = yawnBuffer.filter(v => v >= voteThreshold).length >= effectiveYawnRequired && strongJawOnlyEvidence;
  const boostYawn = marEvidence && yawnSpecificEvidence && !notYawnProfile && !surpriseLikeOpenMouth && ((jawOpen > 0.34 && mouthStretch > 0.24) || yawnScore > 0.60);
  const transitionYawn = avgMar >= yawnMarThreshold * 0.90 && avgEar <= 0.66 && yawnSpecificEvidence && !notYawnProfile && !surpriseLikeOpenMouth;
  if ((!marEvidence || notYawnProfile || surpriseLikeOpenMouth) && !handMouthGesture && !partialYawnWhileCovering && !strongJawOnlyEvidence && !mobileJawSquintEvidence && yawnBuffer.length > 0) {
    yawnBuffer.length = 0;
  }
  const overWithMarEvidence = yawnBuffer.filter(v => v >= voteThreshold).length >= effectiveYawnRequired;
  let yawnDetected = (overWithMarEvidence && marEvidence && yawnSpecificEvidence && !notYawnProfile && !surpriseLikeOpenMouth) || (overWithJawEvidence && !notYawnProfile && !surpriseLikeOpenMouth) || mobileGeometryYawn || boostYawn || durationYawning || (handMouthGesture && !notYawnProfile && !surpriseLikeOpenMouth && (marCondition || yawnScore > 0.35)) || partialYawnWhileCovering || transitionYawn;

  _log(`[EIE][YAWN-DEBUG] provider=${evidenceProvider.id} yawnReq=${effectiveYawnRequired} yawnScore=${Math.round(yawnScore * 100) / 100} over=${over} overMar=${overWithMarEvidence ? 1 : 0} overJaw=${overWithJawEvidence ? 1 : 0} marGate=${marEvidence} yawnEvidence=${yawnSpecificEvidence} surpriseLikeOpen=${surpriseLikeOpenMouth} avgMAR=${Math.round(avgMar * 100) / 100} avgEAR=${Math.round(avgEar * 100) / 100} hold=${yawnHoldFrames} det=${yawnDetected} handFace=${!!handNearFace} handMouth=${handMouthGesture} marCond=${marCondition} earCond=${earCondition} boost=${boostYawn} dur=${durationYawning} partialCover=${partialYawnWhileCovering} transition=${transitionYawn} surpriseGate=${notYawnProfile} strongJaw=${strongJawOnlyEvidence ? 1 : 0} mobileJawSquint=${mobileJawSquintEvidence ? 1 : 0} mobileGeom=${mobileGeometryYawn ? 1 : 0}`);

  if (!notYawnProfile && !surpriseLikeOpenMouth && (jawOpen > 0.25 || mouthStretch > 0.18 || handMouthGesture || partialYawnWhileCovering)) {
    yawnScore = clamp(Math.max(yawnScore, 0.45 + handYawnBoost), 0, 1);
  }

  return { yawnScore, yawnDetected };
}
